import { Icon } from "@iconify/react";
import styled from "styled-components";
import AboutImg from "../../../assets/images/hero/hero-img.jpg";
import { Theme } from "../../../common/themes/theme";
import Eyebrow from "../../../common/components/eyebrow/Eyebrow";
import H2 from "../../../common/components/text/H2";
import Description from "../../../common/components/text/Description";
import { HeadingBlock } from "../../../common/components/layout/HeadingBlock";

const Wrapper = styled.section`
  background: ${Theme.color.background.light};
  font-family: ${Theme.font.fontFamily.sans};
`;

const TopGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 520px;

  @media (max-width: ${Theme.breakpoints.TABLET_SMALL}px) {
    grid-template-columns: 1fr;
  }
`;

const ImageCol = styled.div`
  position: relative;
  overflow: hidden;
  background: ${Theme.color.background.mid};
`;

const FacilityImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  opacity: 0.82;
`;

const ImgOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    transparent 55%,
    rgba(10, 10, 12, 0.7) 100%
  );
`;

const FoundedBadge = styled.div`
  position: absolute;
  bottom: 28px;
  left: 28px;
  background: ${Theme.color.text.primary};
  padding: 14px 20px;
  border-radius: ${Theme.border.borderRadius.sm};
`;

const FoundedNum = styled.p`
  color: ${Theme.color.text.inverse};
  font-size: ${Theme.font.fontSize.xxl};
  font-family: ${Theme.font.fontFamily.heading};
  font-weight: ${Theme.font.fontWeight.bold};
  line-height: ${Theme.font.lineHeight.normal};
  margin: 0 0 2px;
`;

const FoundedLabel = styled.p`
  font-size: ${Theme.font.fontSize.xs};
  font-weight: ${Theme.font.fontWeight.medium};
  letter-spacing: ${Theme.font.letterSpacing.widest};
  text-transform: uppercase;
  color: ${Theme.color.surface.red};
  margin: 0;
`;

const ContentCol = styled.div`
  background: ${Theme.color.surface.light};
  padding: 50px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  border-left: 1px solid ${Theme.color.border.light};

  @media (max-width: ${Theme.breakpoints.TABLET_SMALL}px) {
    padding: 40px 20px;
    border-left: none;
    border-top: 1px solid ${Theme.color.border.light};
  }
`;

const PillarList = styled.div`
  display: flex;
  flex-direction: column;
  margin: 24px 0 0;
  overflow: hidden;
`;

const Pillar = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px 0px;
  background: ${Theme.color.surface.light};

  &:last-child {
    border-bottom: none;
  }
`;

const PillarIcon = styled.div`
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  background: ${Theme.color.surface.red};
  border-radius: ${Theme.border.borderRadius.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${Theme.color.text.primary};
  font-size: 16px;
  margin-top: 1px;

  svg {
    height: 20px;
    width: 20px;
  }
`;

const PillarTitle = styled.p`
  font-family: ${Theme.font.fontFamily.heading};
  font-size: ${Theme.font.fontSize.md};
  font-weight: ${Theme.font.fontWeight.bold};
  text-transform: uppercase;
  color: ${Theme.color.text.heading};
  letter-spacing: ${Theme.font.letterSpacing.wide};
  margin: 0 0 3px;
`;

const PillarDesc = styled.p`
  font-size: ${Theme.font.fontSize.sm};
  color: ${Theme.color.text.body};
  line-height: ${Theme.font.lineHeight.relaxed};
  margin: 0;
`;

const pillars = [
  {
    icon: "la:certificate",
    title: "Certified Quality",
    desc: "ISO 9001, AS9100D, and IATF 16949 certified. Every part ships with full dimensional reports and material traceability.",
  },
  {
    icon: "tabler:clock",
    title: "Fast Turnaround",
    desc: "Prototypes in 3–5 days. Production batches in 10–15 days. On-time delivery rate of 99% across all orders.",
  },
  {
    icon: "tabler:world",
    title: "Global Export Experience",
    desc: "Shipping to Europe, North America, Southeast Asia, and the Middle East with full export documentation support.",
  },
];

function AboutSection() {
  return (
    <Wrapper>
      <TopGrid>
        <ImageCol>
          <FacilityImg src={AboutImg} alt="Aura Metals facility" />
          <ImgOverlay />
          <FoundedBadge>
            <FoundedNum>2020</FoundedNum>
            <FoundedLabel>Year Founded</FoundedLabel>
          </FoundedBadge>
        </ImageCol>

        <ContentCol>
          <HeadingBlock>
            <Eyebrow text={"Who we are"} />
            <H2>
              Built on <span>Precision.</span>
              <br />
              Driven by Excellence.
            </H2>
            <Description>
              Aura Metals is a precision CNC machining manufacturer founded in
              2009. We specialize in complex, high-tolerance components for
              industries where accuracy is non-negotiable. With over 15 years of
              experience, our facility operates 24/7 with a team of 80+
              certified engineers and machinists, serving clients across 20
              countries.
            </Description>
          </HeadingBlock>

          <PillarList>
            {pillars.map((p) => (
              <Pillar key={p.title}>
                <PillarIcon>
                  <Icon icon={p.icon} />
                </PillarIcon>
                <div>
                  <PillarTitle>{p.title}</PillarTitle>
                  <PillarDesc>{p.desc}</PillarDesc>
                </div>
              </Pillar>
            ))}
          </PillarList>
        </ContentCol>
      </TopGrid>
    </Wrapper>
  );
}

export default AboutSection;
